import React, { useEffect, useMemo, useState } from 'react';
import { Button, Input, Select } from 'antd';
import { AppstoreOutlined, LeftOutlined, PlusOutlined, RightOutlined, UploadOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useAssetOverview } from '../../../../../context/AssetOverviewContext';
import { useSaveAssetStatistic, useStatisticEditorOptions } from '../../../../../hooks/useAssetStatistics';
import MapIconsService from '../../../../../services/MapIconService/mapIcon.service';
import { getAssetManagerErrorMessage } from '../../../../../services/AssetManager/assetManager.service';
import { assetManagerServiceMessages } from '../../../../../services/AssetManager/assetManager.constants';
import Notification from '../../../../../shared/components/Notification';
import { NotificationTypes } from '../../../../../enums/notificationTypes';
import { AssetStatisticType, StatisticConnector, type StatisticOperator, type StatisticUnit } from '../../../../../enums/assetStatistic';
import { STATISTIC_UNIT_LABELS } from '../../constants';
import type { AssetLayerField, AssetStatistic, AssetStatisticInput, StatisticConditionValue } from '../../../../../types/InfrastructureManagerTypes';
import WhereConditionRow from './WhereConditionRow';
import UploadIconPopover from './UploadIconPopover';
import QueryFailedPopover from './QueryFailedPopover';
import {
  FIELD_STATISTICS,
  STATISTIC_TYPE_LABELS,
  STATISTIC_TYPES,
  UNIT_OPTIONS,
  VALUELESS_OPERATORS,
  isStatisticSupported,
  type ConditionRow,
  type LibraryIcon,
  type WhereConditionField,
} from './constants';
import styles from './setUpStatisticPopover.module.scss';

interface FormErrors {
  title?: string;
  icon?: string;
  layer?: string;
  statistic?: string;
  field?: string;
  whereConditions?: string;
}

type DetailField = keyof FormErrors;

interface ValidationErrorBody {
  details?: { field: string; message: string }[];
}

const NO_ICONS: LibraryIcon[] = [];

const createConditionRow = (connector: StatisticConnector | null = null): ConditionRow => ({
  id: `where-${Date.now()}-${Math.random()}`,
  connector,
  field: null,
  operator: null,
  value: null,
});

const toConditionRows = (statistic?: AssetStatistic): ConditionRow[] =>
  statistic?.conditions.length
    ? statistic.conditions.map((condition) => ({
        ...createConditionRow(condition.connector),
        ...condition,
        value: condition.value === null ? null : String(condition.value),
      }))
    : [createConditionRow()];

// Values are edited as text; the backend validates them against the field's type.
const toConditionValue = (value: string | null, type: AssetLayerField['type'] | undefined): StatisticConditionValue => {
  if (value === null) return null;
  if (type === 'NUMBER') return Number(value);
  if (type === 'BOOLEAN') return value === 'true';
  return value;
};

// Backend `details[].field` paths, mapped onto the form's error slots.
const toDetailField = (path: string): DetailField | null => {
  if (path === 'title') return 'title';
  if (path === 'iconUrl') return 'icon';
  if (path === 'layerId') return 'layer';
  if (path === 'statistic') return 'statistic';
  if (path === 'field' || path === 'unit') return 'field';
  if (path.startsWith('conditions')) return 'whereConditions';
  return null;
};

interface SetUpStatisticPopoverProps {
  // Prefills the form for editing; omitted when creating.
  statistic?: AssetStatistic;
  onClose: () => void;
}

const SetUpStatisticPopover: React.FC<SetUpStatisticPopoverProps> = ({ statistic, onClose }) => {
  const [view, setView] = useState<'form' | 'upload' | 'queryFailed'>('form');
  // The icon menu opens on its two choices (upload, or the library) and steps into the library list.
  const [isIconMenuOpen, setIsIconMenuOpen] = useState(false);
  const [iconMenuStep, setIconMenuStep] = useState<'choices' | 'library'>('choices');
  const [title, setTitle] = useState(statistic?.title ?? '');
  const [iconUrl, setIconUrl] = useState<string | null>(statistic?.iconUrl ?? null);
  const [layerId, setLayerId] = useState<number | null>(statistic?.layerId ?? null);
  const [statisticType, setStatisticType] = useState<AssetStatisticType | null>(statistic?.statistic ?? AssetStatisticType.COUNT);
  const [field, setField] = useState<string | null>(statistic?.field ?? null);
  const [unit, setUnit] = useState<StatisticUnit | null>(statistic?.unit ?? null);
  const [rows, setRows] = useState<ConditionRow[]>(() => toConditionRows(statistic));
  const [errors, setErrors] = useState<FormErrors>({});

  const { config, isLoadingConfig, isConfigLoadingError } = useAssetOverview();
  const { layers, isLoadingLayers, layersError, fields, isLoadingFields, fieldsError } = useStatisticEditorOptions(layerId);
  // The options need both the layers and the config that narrows them.
  const isLoadingLayerOptions = isLoadingLayers || isLoadingConfig;
  const { saveStatistic, isSavingStatistic } = useSaveAssetStatistic();
  const { getMapIcons } = MapIconsService();
  const { data: icons = NO_ICONS } = useQuery<LibraryIcon[], Error>(getMapIcons());

  useEffect(() => {
    const error = layersError ?? fieldsError;
    if (!error) return;
    Notification({
      message: layersError ? assetManagerServiceMessages.LIST_CONFIG.LAYERS_ERROR : assetManagerServiceMessages.LIST_CONFIG.FIELDS_ERROR,
      description: getAssetManagerErrorMessage(error),
      type: NotificationTypes.ERROR,
    });
  }, [layersError, fieldsError]);

  // Without the config no layer qualifies, so say why instead of showing an unexplained empty list.
  useEffect(() => {
    if (isConfigLoadingError) Notification({ message: assetManagerServiceMessages.LIST_CONFIG.LOAD_ERROR, type: NotificationTypes.ERROR });
  }, [isConfigLoadingError]);

  const geometry = layers.find(({ id }) => id === layerId)?.geometryType;
  const isFieldStatistic = !!statisticType && FIELD_STATISTICS.has(statisticType);
  const unitOptions = (statisticType && UNIT_OPTIONS[statisticType]) ?? [];

  // Only the layers in the asset list config; a saved statistic keeps its own layer even if it has since left the config.
  const savedLayerId = statistic?.layerId;
  const layerOptions = useMemo(() => {
    const configured = new Set(config?.rows.map((row) => row.layerId));
    return layers.filter(({ id }) => configured.has(id) || id === savedLayerId).map(({ id, name }) => ({ value: id, label: name }));
  }, [layers, config, savedLayerId]);
  const statisticOptions = useMemo(
    () =>
      STATISTIC_TYPES.filter((type) => !geometry || isStatisticSupported(geometry, type)).map((type) => ({
        value: type,
        label: STATISTIC_TYPE_LABELS[type],
      })),
    [geometry],
  );
  const fieldTypes = useMemo(() => new Map(fields.map(({ name, type }) => [name, type])), [fields]);
  const numericFieldOptions = useMemo(
    () => fields.filter(({ type }) => type === 'NUMBER').map(({ name }) => ({ value: name, label: name })),
    [fields],
  );
  const unitSelectOptions = useMemo(() => unitOptions.map((option) => ({ value: option, label: STATISTIC_UNIT_LABELS[option] })), [unitOptions]);

  // A saved icon missing from the library (e.g. removed later) still shows, rather than a raw URL.
  const iconSelectOptions = useMemo(() => {
    const options = icons.map((icon) => ({ value: icon.url, label: icon.name }));
    if (iconUrl && !icons.some((icon) => icon.url === iconUrl)) options.unshift({ value: iconUrl, label: 'Current icon' });
    return options;
  }, [icons, iconUrl]);

  const clearError = (key: keyof FormErrors) => setErrors((current) => ({ ...current, [key]: undefined }));

  const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(event.target.value);
    clearError('title');
  };

  const handleIconMenuOpenChange = (open: boolean) => {
    setIsIconMenuOpen(open);
    if (open) setIconMenuStep('choices');
  };

  const handleIconClear = () => setIconUrl(null);

  const handleUploadChoice = () => {
    setIsIconMenuOpen(false);
    setView('upload');
  };

  const handleLibraryChoice = () => setIconMenuStep('library');

  const handleIconMenuBack = () => setIconMenuStep('choices');

  const handleLibraryIconClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setIconUrl(event.currentTarget.dataset.url ?? null);
    clearError('icon');
    setIsIconMenuOpen(false);
  };

  const handleUploadConfirm = (uploadedUrl: string) => {
    setIconUrl(uploadedUrl);
    clearError('icon');
    setView('form');
  };

  const handleBackToForm = () => setView('form');

  // A different layer has different fields and geometry, so everything that depends on them resets.
  const handleLayerChange = (value: number | undefined) => {
    const nextGeometry = layers.find(({ id }) => id === value)?.geometryType;
    setLayerId(value ?? null);
    setField(null);
    setRows([createConditionRow()]);
    if (statisticType && nextGeometry && !isStatisticSupported(nextGeometry, statisticType)) {
      setStatisticType(AssetStatisticType.COUNT);
      setUnit(null);
    }
    clearError('layer');
  };

  const handleStatisticChange = (value: AssetStatisticType | undefined) => {
    setStatisticType(value ?? null);
    setField(null);
    setUnit((value && UNIT_OPTIONS[value]?.[0]) ?? null);
    clearError('statistic');
    clearError('field');
  };

  const handleFieldChange = (value: string | undefined) => {
    setField(value ?? null);
    clearError('field');
  };

  const handleUnitChange = (value: StatisticUnit | undefined) => {
    setUnit(value ?? null);
    clearError('field');
  };

  const handleWhereFieldChange = (index: number, key: WhereConditionField, value: string | null) => {
    setRows((current) =>
      current.map((row, i) => {
        if (i !== index) return row;
        // A new field may not allow the old operator or value, and a new operator takes a different kind of input.
        if (key === 'field') return { ...row, field: value, operator: null, value: null };
        if (key === 'operator') return { ...row, operator: value as StatisticOperator | null, value: null };
        if (key === 'connector') return { ...row, connector: value as StatisticConnector | null };
        return { ...row, value };
      }),
    );
    clearError('whereConditions');
  };

  const handleAddWhereCondition = () => setRows((current) => [...current, createConditionRow(StatisticConnector.AND)]);

  const handleRemoveWhereCondition = (index: number) => {
    setRows((current) => current.filter((_, i) => i !== index));
    clearError('whereConditions');
  };

  const isRowComplete = (row: ConditionRow) => !!row.field && !!row.operator && (VALUELESS_OPERATORS.has(row.operator) || !!row.value?.trim());

  // The value box takes free text, so a number field's value is checked here rather than sent as NaN.
  const isRowValueValid = (row: ConditionRow) => {
    if (!row.operator || VALUELESS_OPERATORS.has(row.operator) || fieldTypes.get(row.field ?? '') !== 'NUMBER') return true;
    return Number.isFinite(Number(row.value?.trim()));
  };

  const validate = (): FormErrors => {
    const nextErrors: FormErrors = {};
    if (!title.trim()) nextErrors.title = 'Title is required';
    if (!iconUrl) nextErrors.icon = 'Select an icon';
    if (layerId === null) nextErrors.layer = 'Select a layer';
    if (!statisticType) nextErrors.statistic = 'Select a statistic type';
    if (isFieldStatistic && !field) nextErrors.field = 'Select a field';
    if (unitOptions.length && !unit) nextErrors.field = 'Select a unit';
    const invalidRow = rows.find((row) => isRowComplete(row) && !isRowValueValid(row));
    if (!rows.every(isRowComplete)) nextErrors.whereConditions = 'Complete every query condition';
    else if (invalidRow) nextErrors.whereConditions = `"${invalidRow.field}" needs a number`;
    return nextErrors;
  };

  const buildInput = (): AssetStatisticInput => {
    return {
      title: title.trim(),
      iconUrl: iconUrl ?? '',
      layerId: layerId ?? 0,
      statistic: statisticType ?? AssetStatisticType.COUNT,
      field: isFieldStatistic ? field : null,
      unit: unitOptions.length ? unit : null,
      conditions: rows.map((row, index) => {
        const operator = row.operator as StatisticOperator;
        const value = VALUELESS_OPERATORS.has(operator) ? null : toConditionValue(row.value?.trim() ?? null, fieldTypes.get(row.field ?? ''));
        return { connector: index === 0 ? null : row.connector, field: row.field ?? '', operator, value };
      }),
    };
  };

  // Field-level validation lands next to its field; a rejected query gets the query-failed view.
  const showSaveErrors = (error: unknown) => {
    const details = (error as AxiosError<ValidationErrorBody>)?.response?.data?.details ?? [];
    const mapped = details.reduce<FormErrors>((result, detail) => {
      const key = toDetailField(detail.field);
      return key ? { ...result, [key]: detail.message } : result;
    }, {});
    setErrors(mapped);
    if (mapped.whereConditions) setView('queryFailed');
  };

  const handleSave = async () => {
    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    try {
      await saveStatistic(buildInput(), statistic?.id);
      onClose();
    } catch (error) {
      showSaveErrors(error);
    }
  };

  const handleSaveClick = () => void handleSave();

  if (view === 'upload') {
    return <UploadIconPopover onBack={handleBackToForm} onConfirm={handleUploadConfirm} />;
  }

  if (view === 'queryFailed') {
    return <QueryFailedPopover onBack={handleBackToForm} />;
  }

  const renderIconOption = (option: { value?: string | number; label?: React.ReactNode }) => (
    <span className={styles.iconOption}>
      <img src={String(option.value)} alt="" loading="lazy" className={styles.iconOptionImage} />
      {option.label}
    </span>
  );

  const renderIconMenu = () =>
    iconMenuStep === 'choices' ? (
      <ul className={styles.iconMenu}>
        <li>
          <button type="button" className={styles.iconMenuItem} onClick={handleUploadChoice}>
            <UploadOutlined />
            Upload image
          </button>
        </li>
        <li>
          <button type="button" className={styles.iconMenuItem} onClick={handleLibraryChoice}>
            <AppstoreOutlined />
            Choose from library
            <RightOutlined className={styles.iconMenuChevron} />
          </button>
        </li>
      </ul>
    ) : (
      <div className={styles.iconMenu}>
        <button type="button" className={styles.iconMenuItem} onClick={handleIconMenuBack}>
          <LeftOutlined />
          Back
        </button>
        {iconSelectOptions.length ? (
          <ul className={styles.iconLibraryList}>
            {iconSelectOptions.map(({ value, label }) => (
              <li key={value}>
                <button
                  type="button"
                  data-url={value}
                  className={`${styles.iconMenuItem} ${value === iconUrl ? styles.iconMenuItemSelected : ''}`}
                  onClick={handleLibraryIconClick}
                >
                  <img src={value} alt="" loading="lazy" className={styles.iconOptionImage} />
                  {label}
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <span className={styles.iconMenuEmpty}>No icons in the library yet.</span>
        )}
      </div>
    );

  return (
    <div className={styles.container}>
      <div className={styles.scrollArea}>
        <div className={styles.header}>SET UP STATISTIC</div>

        <div className={styles.row}>
          <div className={styles.field}>
            <span className={styles.label}>TITLE:</span>
            <Input
              className={styles.titleInput}
              placeholder="Enter text to be displayed"
              value={title}
              onChange={handleTitleChange}
              status={errors.title ? 'error' : ''}
            />
            {errors.title && <span className={styles.errorText}>{errors.title}</span>}
          </div>
          <div className={styles.field}>
            <span className={styles.label}>SELECT ICON:</span>
            <Select
              className={styles.iconSelect}
              allowClear
              popupClassName={styles.selectDropdown}
              popupMatchSelectWidth={false}
              placeholder="Select icon"
              open={isIconMenuOpen}
              onDropdownVisibleChange={handleIconMenuOpenChange}
              dropdownRender={renderIconMenu}
              value={iconUrl ?? undefined}
              options={iconSelectOptions}
              labelRender={renderIconOption}
              onClear={handleIconClear}
              status={errors.icon ? 'error' : ''}
            />
            {errors.icon && <span className={styles.errorText}>{errors.icon}</span>}
          </div>
        </div>

        <div className={styles.row}>
          <div className={styles.field}>
            <span className={styles.label}>SELECT FROM:</span>
            <Select
              className={styles.select}
              popupClassName={styles.selectDropdown}
              popupMatchSelectWidth={false}
              placeholder="Layer"
              allowClear
              showSearch
              optionFilterProp="label"
              value={layerId ?? undefined}
              options={layerOptions}
              loading={isLoadingLayerOptions}
              notFoundContent={isLoadingLayerOptions ? 'Loading…' : layerOptions.length ? undefined : 'Add layers to the asset list first'}
              onChange={handleLayerChange}
              status={errors.layer ? 'error' : ''}
            />
            {errors.layer && <span className={styles.errorText}>{errors.layer}</span>}
          </div>
          <div className={styles.field}>
            <span className={styles.label}>STATISTIC:</span>
            <Select
              className={styles.select}
              popupClassName={styles.selectDropdown}
              popupMatchSelectWidth={false}
              placeholder="Count"
              allowClear
              value={statisticType ?? undefined}
              options={statisticOptions}
              onChange={handleStatisticChange}
              status={errors.statistic ? 'error' : ''}
            />
            {errors.statistic && <span className={styles.errorText}>{errors.statistic}</span>}
            {isFieldStatistic && (
              <>
                <span className={styles.ofLabel}>OF</span>
                <Select
                  className={styles.select}
                  popupClassName={styles.selectDropdown}
                  popupMatchSelectWidth={false}
                  placeholder="Field"
                  allowClear
                  value={field ?? undefined}
                  options={numericFieldOptions}
                  loading={isLoadingFields}
                  notFoundContent={isLoadingFields ? 'Loading…' : undefined}
                  onChange={handleFieldChange}
                  status={errors.field ? 'error' : ''}
                  disabled={layerId === null}
                />
              </>
            )}
            {unitOptions.length > 0 && (
              <>
                <span className={styles.ofLabel}>IN</span>
                <Select
                  className={styles.select}
                  popupClassName={styles.selectDropdown}
                  popupMatchSelectWidth={false}
                  placeholder="Unit"
                  allowClear
                  value={unit ?? undefined}
                  options={unitSelectOptions}
                  onChange={handleUnitChange}
                  status={errors.field ? 'error' : ''}
                />
              </>
            )}
            {errors.field && <span className={styles.errorText}>{errors.field}</span>}
          </div>
        </div>

        <div className={styles.whereSection}>
          <span className={styles.label}>WHERE:</span>
          <div className={styles.whereRows}>
            {rows.map((row, index) => (
              <WhereConditionRow
                key={row.id}
                index={index}
                row={row}
                layerId={layerId}
                fields={fields}
                isLoadingFields={isLoadingFields}
                dropdownClassName={styles.selectDropdown}
                onChangeField={handleWhereFieldChange}
                onRemove={handleRemoveWhereCondition}
              />
            ))}
          </div>
          <button type="button" className={styles.addButton} onClick={handleAddWhereCondition} aria-label="Add condition">
            <PlusOutlined />
          </button>
        </div>
      </div>

      <div className={styles.footer}>
        {errors.whereConditions && <span className={styles.errorText}>{errors.whereConditions}</span>}
        <Button className={styles.saveButton} onClick={handleSaveClick} loading={isSavingStatistic}>
          Save
        </Button>
      </div>
    </div>
  );
};

export default SetUpStatisticPopover;
